import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCGrid, RCCHookToast, RCCInput, RCCRangeSlider, RCCRefModalProps, RCCServicesApi, RCCTextArea} from "react-common-components";
import { useFieldArray, useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Audit from "src/components/Audit";
import Tabs from "src/components/Tabs";
import LovSectors from "src/components/Lov/Sectors";
import MetricsRanges from "src/components/MetricsRanges";
import stringToNumber from "src/utils/stringToNumber";
import Select from "src/components/Select";
import AssociateIndicators from "src/components/AssociateIndicators";
import SwotItemBonds from "src/pages/Analysis/SWOT/components/SWOTItens/components/ItemDetail/bonds";
import Title from "src/components/Title";
import { CalculatePercentageType, IMetba003, IModalObjective,IRefObjectivesModals } from "./interface";
import addAndChangeSchemaObjectives from "./schemas";
import { IMetba002 } from "../Perspective/interface";
import TabObjectiveProject from "./tabProjetcs";
import TabStrategies from "../TabStrategies";

const ModalsObjective: React.ForwardRefRenderFunction<IRefObjectivesModals, IModalObjective> = ({
	onCallbackMap
},ref) => {
	const { translate, currentLanguage } = useTranslation();
	const {addToast} = RCCHookToast();
	const { 
		handleSubmit: handleSubmitAddOrChange,
		register : registerAddOrChange,
		unregister: unregisterAddOrChange,
		control: controlAddOrChange,
		reset : resetAddOrChange,
		setError : setErrorAddOrChange, 
		setValue: setValueAddorChange,
		formState: { errors : errorsAddOrChange },
		clearErrors: clearErrorsAddOrChange 
	} = useForm();

	const { 
		fields: fieldsMetricsRange, 
		remove: removeMetricsRange,
		append: appendMetricsRange
	} = useFieldArray({
		control: controlAddOrChange,
		name: "metba006"
	});

	const [perspectiveSelected, setPerspectiveSelected] = useState<IMetba002>({} as IMetba002);
	const [objectiveSelected, setObjectiveSelected] = useState<IMetba003>({} as IMetba003);

	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChangeObjectives = useRef<RCCRefModalProps>(null);

	const openModal = (perspective: IMetba002, objective?: IMetba003) => {
		if(objective){
			actionButtonChange(perspective, objective);
			return;
		}

		actionButtonAdd(perspective);
	};

	const actionButtonAdd = (perspective: IMetba002) => {
		resetAddOrChange(); 
		setIsAdd(true);
		setObjectiveSelected({} as IMetba003);
		setPerspectiveSelected(perspective);
		appendMetricsRange([
			{mea006Cor:"#D91E18", mea006Valorini:0, mea006Valorfim:80.99},
			{mea006Cor:"#F5AB35", mea006Valorini:81, mea006Valorfim:99.99 },
			{mea006Cor:"#019875", mea006Valorini:100, mea006Valorfim:9999 }
		]);
		modalAddOrChangeObjectives.current?.openModal();
	};

	const actionButtonChange = (perspective: IMetba002, objective: IMetba003) => {
		resetAddOrChange(); 
		setIsAdd(false);
		setPerspectiveSelected(perspective);
		getObjective(objective.mea003Id, perspective.mea002Mea001Id);
	};
	async function getObjective(objectiveId: number, perspectiveId: number){
		try{
			const result = await RCCServicesApi.getRequest({
				method: "Objectives/Select",
				urlParameters: {
					id: objectiveId,
					mea001Id: perspectiveId
				}
			});
			setObjectiveSelected(result.data);
			appendMetricsRange(result.data.metba006);
			modalAddOrChangeObjectives.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar objetivos"), addToast);
		}
	}
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChangeObjectives.current?.setLoadingButton();

			const convertNumber = (dataX: any) => dataX.mea004Id !== undefined && Number(dataX.mea004Id) ? parseInt(dataX.mea004Id, 10) : 0;

			const dataForm: IMetba003 = {
				...data,
				Mea002Mea001Id: perspectiveSelected.mea002Mea001Id || 0,
				mea003Mea002Id: perspectiveSelected?.mea002Id || 0,
				mea003Mea002Nome: perspectiveSelected?.mea002Nome || "",
				mea003Taa001Id: Number(data.mea003Taa001Id) === 0 ? undefined : Number(data.mea003Taa001Id),
				mea003Taa006Id: Number(data.mea003Taa006Id) === 0 ? undefined : Number(data.mea003Taa006Id),
				metba004: data.metba004?.map((mea004: any, idx: number) => ({
					...mea004,
					mea004Id: mea004.iqc001Id === undefined ? 0 : convertNumber(mea004),
					mea004Fatorsucesso: mea004.mea004Fatorsucesso ? "S" : "N",
					mea004Ordem: idx + 1
				})).filter((e: any) => e.mea004Id !== "0"),
				metbe001: data.metbe001?.map((metbe: any) => ({
					mee001Id: metbe.mee001Id
				})).filter((e: any) => e.mee001Id !== 0),
				metba006: data.metba006?.map((metric: any) => ({
					...metric,
					mea006Cor: metric.mea006Cor.replace("#", ""),
					mea006Valorini: typeof metric.mea006Valorini === "string" ? stringToNumber(metric.mea006Valorini,currentLanguage) || 0 : metric.mea006Valorini,
					mea006Valorfim: typeof metric.mea006Valorfim === "string" ? stringToNumber(metric.mea006Valorfim,currentLanguage) || 0 : metric.mea006Valorfim
				}))
			};
			if(data.mea003Peso !== 0 && (!data.mea003Peso || data.mea003Peso === undefined)){
				dataForm.mea003Peso = isAdd ? 0 : objectiveSelected.mea003Peso || 0;
			}
			await addAndChangeSchemaObjectives(translate, currentLanguage).validate({...dataForm}, {
				abortEarly: false
			});

			if(isAdd){
				const result = await RCCServicesApi.postRequest({
					method: "Objectives/Insert",
					body: {
						...dataForm,
						mea003Id: 0		
					}
				});

				dataForm.id = String(result.data);
				dataForm.mea003Id = result.data;
				
				if(onCallbackMap)
				    onCallbackMap();

			}else{
				await RCCServicesApi.putRequest({
					method: "Objectives/Update",
					body: {
						...dataForm
					}
				});

				if(onCallbackMap)
				    onCallbackMap();
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Objetivo adicionado com sucesso") : translate("Objetivo alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChangeObjectives.current?.resetLoadingButton();
			modalAddOrChangeObjectives.current?.closeModal();

		}catch(err){
			modalAddOrChangeObjectives.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange)){
				addToast({
					color: "yellow",
					description: translate("Verifique os campos e tente novamente"),
					title: translate("Atenção")
				});
				return;
			}
				
			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar objetivo") : translate("Erro ao alterar objetivo"), addToast);
		}
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));

	return (
		<>
			{/* Modal Add an Change */}
			<Modal
				ref={modalAddOrChangeObjectives}
				header={{
					title: isAdd ? translate("Adicionar Objetivo") : translate("Alterar Objetivo"),
					subtitle: isAdd ? `${translate("Perspectiva")}: ${perspectiveSelected?.mea002Nome}` : `${translate("Objetivo")}: ${objectiveSelected?.mea003Nome}`,
					icon: {
						name: "mapMarker"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
			>
				<Tabs 
					name="tabObjectives"
					tabInit={0}
					options={[
						{
							label:translate("Geral"),
							content:
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={8}
								>
									<RCCGrid
										container
										item
										alignItems="center"
										justifyContent="flex-end"
									>
										{
											objectiveSelected && <Audit data={objectiveSelected} type="pop"/>
										}
									</RCCGrid>
									<RCCGrid styleInline="display: none">
										<RCCInput 
											register={registerAddOrChange}
											name="mea003Id"
											label=""
											defaultValue={objectiveSelected?.mea003Id}
											readOnly
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
									>
										<RCCInput 
											register={registerAddOrChange}
											name="mea003Nome"
											label={translate("Nome")}
											maxLength={100}
											defaultValue={objectiveSelected?.mea003Nome}
											error={errorsAddOrChange.mea003Nome}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
									>
										<RCCTextArea 
											register={registerAddOrChange}
											name="mea003Descricao"
											label={translate("Descrição")}
											maxLength={200}
											defaultValue={objectiveSelected?.mea003Descricao}
											error={errorsAddOrChange.mea003Descricao}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
									>
										<Select 
											control={controlAddOrChange}
											error={errorsAddOrChange.mea003Calcperc}
											name="mea003Calcperc"
											label={translate("Calcular percentual por")}
											options={[{
												value: CalculatePercentageType.INDICATORS,
												label: translate("Indicadores")
											},{
												value: CalculatePercentageType.ACTIONS,
												label: translate("Ações")
											},{
												value: CalculatePercentageType.INDICATORS_ACTIONS,
												label: translate("Indicadores e Ações")
											},{
												value: CalculatePercentageType.PROJECTS,
												label: translate("Projetos")
											},{
												value: CalculatePercentageType.INDICATORS_PROJECTS,
												label: translate("Indicadores e Projetos")
											},{
												value: CalculatePercentageType.PROJECTS_ACTIONS,
												label: translate("Projetos e Ações")
											},{
												value: CalculatePercentageType.INDICATORS_PROJECTS_ACTIONS,
												label: translate("Indicadores, Projetos e Ações")
											}]}
											defaultValue={objectiveSelected?.mea003Calcperc ? String(objectiveSelected?.mea003Calcperc) : CalculatePercentageType.ACTIONS}
											required
										/>
									</RCCGrid>
									<RCCGrid
										container
										item
									>
										<LovSectors 
											control={controlAddOrChange}
											name="mea003Taa001Id"
											label={translate("Setor")}
											defaultValue={objectiveSelected && objectiveSelected.mea003Taa001Id ? String(objectiveSelected.mea003Taa001Id) : ""}
											isCreatable
											isClearable
										/>
									</RCCGrid>
									<RCCGrid 
										container
										item>
										<RCCRangeSlider
											control={controlAddOrChange}
											name="mea003Peso"
											color="purple"
											label={translate("Peso")}
											valueLabelDisplay="on"
											defaultValue={!isAdd ? objectiveSelected.mea003Peso : 1}
											marks={[
												{
													value: 0,
													label: "0"
												},
												{
													value: 100,
													label: "100"
												}
											]}
											min={0}
											max={100}
											styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
										/>
									</RCCGrid>
								</RCCGrid>
							</RCCGrid>
						},{
							label:translate("Métricas"),
							content:
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={10}
								>
									<MetricsRanges
										names ={{
											table:"metba006",
											idName: "mea006Id",
											initValueName:"mea006Valorini",
											endValueName:"mea006Valorfim",
											colorName:"mea006Cor",
											metricIdName:"mea003Taa006Id"
										}}
										source={fieldsMetricsRange}
										control={controlAddOrChange}
										remove={removeMetricsRange}
										append={appendMetricsRange}
										errors={errorsAddOrChange}
										clearErrors={clearErrorsAddOrChange}
										setValue={setValueAddorChange}
										metricId={objectiveSelected.mea003Taa006Id?.toString()}
										useMetricLov
									/>
								</RCCGrid>
							</RCCGrid>
						},{
							label:translate("Indicadores"),
							content:
							<RCCGrid
								container
								item
								direction="column"
								alignItems="center"
							>
								<RCCGrid
									container
									item
									xs={12}
									sm={8}
								>
									<AssociateIndicators 
										control={controlAddOrChange}
										error={errorsAddOrChange}
										indicatorsDefaultValue={objectiveSelected.metba004 ? objectiveSelected.metba004 : []}
										disabled={false}
										order
										register={registerAddOrChange}
										unregister={unregisterAddOrChange}
										clearErrors={clearErrorsAddOrChange}
										names={{
											nameOfID: "mea004Id",
											nameOfObject: "metba004",
											nameOfRangeSlider: "mea004Peso",
											nameOfSelectIndicator: "mea004Iqc001Id",
											nameOfCheckbox: "mea004Fatorsucesso"
										}}
									/>
								</RCCGrid>
							</RCCGrid>
						},{
							label:translate("SWOT"),
							content:
							<RCCGrid container item>
								{objectiveSelected.metbs002 && objectiveSelected.metbs002.length > 0 ? 
									<>
										{objectiveSelected.metbs002.map((swotGroup: any, index: any) => 
											<>
												<Title
													color="purple"
													size="md"
													marginTop={index === 0 ? 1 : 20}
												>
													{swotGroup[0].mes001Titulo}
												</Title>
												<hr/>
												<SwotItemBonds swotItens={swotGroup || []}/>
											</>
										)}
									</>
									:
									<RCCAlert
										description={translate("Nenhum vinculo de item encontrado")}
									/>
								}
							</RCCGrid>
						},{
							label:translate("Estratégias"),
							content: (
								<RCCGrid
									container
									item
									direction="column"
									alignItems="center"
								>
									<RCCGrid
										container
										item
										xs={12}
										sm={8}
									>
										<TabStrategies 
											control={controlAddOrChange}
											error={errorsAddOrChange}  
											unregister={unregisterAddOrChange}
											clearErrors={clearErrorsAddOrChange}
											disabled={false}
											names={{
												nameOfObject: "metbe001"
											}}
											mapId={perspectiveSelected.mea002Mea001Id}
											objectiveId={objectiveSelected.mea003Id}
											objectives={objectiveSelected.metbe001}
										/>
									</RCCGrid>
								</RCCGrid>)
						},{
							label:translate("Projetos"),
							content:(
								<>
									{objectiveSelected.prtba005 &&  objectiveSelected.prtba005.length > 0 ? (
										<TabObjectiveProject list={objectiveSelected.prtba005 ? objectiveSelected.prtba005 : []}/>
									) : (
										<RCCAlert
											description={translate("Nenhum vinculo de item encontrado")}
										/>
									)}
								</>
							)
						}]}>
				</Tabs>
				
			</Modal>
		</>
	);
};

export default forwardRef(ModalsObjective);
